/* eslint-disable camelcase */

import { ElementMetaData, File, ImageMetaData } from './contentContract';

export interface Links {
  site: string;
  lab: string;
  shop: string;
  trialAccessUrl: string;
  privacyurl: string;
  cookiesurl: string;
  contentFeedbackLink: string;
}

export interface Localization {
  system: string;
  content: string;
}

export interface Metadata {
  title: string;
  subtitle: string;
  copyright: string;
  copyright_year: string;
  edition_number: string;
  editor: string;
  isbn13: string;
  isbn13_ebook: string;
  identifier_ebook: string;
  author: string;
  coverImage: string;
  links: Links;
  localization: Localization;
}

export interface Info {
  id: string;
  company: string;
  usergroups: string;
  experimentalCompaniesEnabled: string;
  userName: string;
  email: string;
}

export interface WeAlsoNeed {
  externalUserId: string;
}

export interface User {
  info: Info;
  we_also_need: WeAlsoNeed;
}

export interface Sso {
  url_account_home: string;
  url_activate_ekey: string;
  url_edit_profile: string;
  url_new_account: string;
  url_signin: string;
  url_signout: string;
  sso_domain: string;
  sso_cookie_domain: string;
  myaccountId: string;
  myaccount_authtoken: string;
}

export interface Environment {
  customer: string;
  featureflags: any[];
  stackpurpose: string;
  installationpurpose: string;
  type: string;
  context: string;
  backendUser?: number;
}

export interface TeacherMenuLink {
  pid: number;
  uid: number;
  title: string;
  link: string;
  target: string;
  active: number;
  current: number;
  spacer: number;
  children: TeacherMenuLink[];
  accessGranted: boolean;
  fe_group: string;
}

export interface TeacherMenu {
  accessGranted: boolean;
  fe_group: string;
  uid: number;
  link: string;
  linktext: string;
  title: string;
  children: TeacherMenuLink[];
}

export interface Settings {
  useCourseBuilder: number;
  searchPageId: number;
  teacherMenu?: TeacherMenu;
}

export interface Colors {
  base1: string;
  base2: string;
  background: string;
  contrast: string;
  text: string;
  light: boolean;
  title: string;
}

export interface Font {
  name: string;
  url?: string;
  defaultSize: string;
}

export interface Fonts {
  title: Font;
  system: Font;
  content: Font;
}

export interface SiteLogo {
  logo: string;
  brandlogo: string;
  favicon: string;
  url: string;
}

export interface AdditionalLogo {
  logo: string;
  url: string;
}

export interface SiteAppearance {
  theme: string;
  colors: Colors;
  fonts: Fonts;
  logo: SiteLogo;
  additionalLogo?: AdditionalLogo;
}

export interface Site {
  metadata: Metadata;
  user: User;
  sso: Sso;
  environment: Environment;
  settings: Settings;
  appearance: SiteAppearance;
}

export interface Robots {
  noIndex: boolean;
  noFollow: boolean;
}

export interface Meta {
  title: string;
  subtitle: string;
  abstract: string;
  description: string;
  keywords: string;
  canonical: string;
  robots: Robots;
  author: string;
  feGroup: string;
}

export interface Breadcrumb {
  active: number;
  current: number;
  link: string;
  spacer: number;
  hasSubpages: boolean;
  target: string;
  title: string;
}

export interface PageAppearance {
  layout: string;
  backendLayout: string;
  backendLayoutNextLevel: string;
  newUntil: string;
}

export interface System {
  tstamp: number;
  crdate: number;
  starttime: number;
  endtime: number;
  feGroup: string;
  sorting: number;
}

export interface SectionIndex {
  collapsible: boolean;
  minNumberOfSections: number;
  numberOfLevels: number;
}

export interface Messages {
  underConstruction: string;
}

export interface PageSettings {
  sectionIndex: SectionIndex;
  messages: Messages;
}

export interface Page {
  id: number;
  pid: number;
  title: string;
  type: string;
  meta: Meta;
  breadcrumbs: Breadcrumb[];
  appearance: PageAppearance;
  system: System;
  pageSettings: PageSettings;
  editLink: string;
  discontinued?: string;
  parentChapterTitle?: string;
  parentChapterUid?: string;
}

export interface ColPosAppearance {
  layout: string;
  frameClass: string;
  spaceBefore: string;
  spaceAfter: string;
  extendedcmsElementType: string;
  extendedcmsElementSubType: string;
  extendedCmsClassnames: string;
  extendedCmsSummary: string;
  academicStyling: boolean;
  sectionIndex: boolean;
}

export interface Position {
  horizontal: string;
  vertical: string;
  noWrap: boolean;
}

export interface Count {
  files: number;
  columns: number;
  rows: number;
}

export interface Border {
  enabled: boolean;
  width: number;
  padding: number;
}

export interface Responsive {
  desktop: File;
  wide: File;
}

export interface Gallery {
  position: Position;
  width: number;
  count: Count;
  equalMediaHeight: boolean;
  equalMediaWidth: boolean;
  maxGalleryWidth: number;
  columnSpacing: number;
  border: Border;
  rows: any;
  responsive: Responsive;
}

export interface ColPosContent {
  imagecaption: string;
  fe_group: number;
  header: string;
  subheader: string;
  headerLayout: number;
  headerPosition: string;
  headerLink: string;
  gallery: Gallery;
  bodytext: string;
  error: string;
  carousel: boolean;
  elementMetaData: ElementMetaData;
  enlargeImageOnClick: boolean;
  imageMetaData: ImageMetaData;
  template: string;
  label?: string;
}

export interface ColPos0 {
  appearance: ColPosAppearance;
  id: number;
  pid: number;
  type: string;
  colPos: number;
  categories: string;
  fe_group: number;
  content: ColPosContent;
  editLink: string;
}

export interface Content {
  colPos0: ColPos0[];
  colPos1: ColPos0[];
  colPos2: ColPos0[];
  colPos4: ColPos0[];
}

export interface Glossary {
  term_key: string;
  term: string;
  term_id: string;
  term_display: string;
  alternates: Array<string>;
  description: string;
  descriptionExtra: string;
  images: string;
  media: string;
}

export enum PageType {
  twocolumns = 'twocolumns',
  content = 'content'
}

export interface RootPage {
  content: Content;
  glossaries: Glossary;
  page: Page;
  status?: number;
  dummy?: string;
  pageType?: PageType;
}
